import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Seo from '../components/Seo';
import { apiGet, formatEuro } from '../lib/api';
import { trackEvent } from '../lib/analytics';
import { getCheckoutSessionIdFromLocation, hasTrackedPurchase, markPurchaseTracked } from '../lib/checkout';
import { LEGAL_PAGE_SEO } from '../lib/seo';
import './Shop.css';

const SuccessPage = ({ onCheckoutSuccess }) => {
  const { search } = useLocation();
  const sessionId = getCheckoutSessionIdFromLocation(search);
  const callbackRef = useRef(onCheckoutSuccess);
  callbackRef.current = onCheckoutSuccess;
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(0);
  const [cartUpdated, setCartUpdated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setResult(null);
    const verify = async () => {
      if (!sessionId) {
        setError('Manca il riferimento del pagamento. Controlla la conferma ricevuta via email.');
        setLoading(false);
        return;
      }
      try {
        const confirmation = await apiGet(`/api/checkout/confirmation?session_id=${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        if (confirmation?.sessionId !== sessionId || !confirmation.orderId || !Array.isArray(confirmation.items)) {
          throw new Error('Non è stato possibile verificare questo pagamento.');
        }
        setResult(confirmation);
        if (confirmation.paymentStatus !== 'paid') return;
        setCartUpdated(Boolean(callbackRef.current?.(confirmation)));
        if (!hasTrackedPurchase(confirmation.orderId)) {
          trackEvent('purchase', {
            transaction_id: confirmation.orderId, currency: confirmation.currency,
            value: confirmation.subtotalCents / 100, shipping: confirmation.shippingCents / 100,
            items: confirmation.items.map(item => ({
              item_id: item.productId, item_name: item.name,
              price: item.unitPriceCents / 100, quantity: item.qty
            }))
          });
          markPurchaseTracked(confirmation.orderId);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Verifica pagamento non disponibile.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [sessionId, retry]);

  const paid = result?.paymentStatus === 'paid';
  const title = loading ? 'Verifica del pagamento…' : paid ? 'Grazie per il tuo ordine.' :
    result?.paymentStatus === 'expired' ? 'Sessione di pagamento scaduta' : 'Pagamento da verificare';

  return (
    <section className="success-page">
      <Seo title={LEGAL_PAGE_SEO.success.title} description={LEGAL_PAGE_SEO.success.description}
        path="/shop/success" robots="noindex,nofollow" />
      <p className="page-eyebrow">Panetteria Primavera</p>
      <h2 className="shop-title">{title}</h2>
      <div aria-live="polite">
        {loading && <p className="success-meta">Stiamo controllando l’esito con il servizio di pagamento.</p>}
        {error && <p className="shop-state shop-error" role="alert">{error} Il carrello è conservato.</p>}
        {!loading && result && !paid && <p className="success-meta">Non risulta ancora un pagamento completato per questa sessione. Puoi ricontrollare o tornare al carrello.</p>}
        {paid && <>
          <p className="success-lead">Pagamento completato. Riceverai conferma e aggiornamenti via email.</p>
          <p className="success-meta">Riferimento ordine: <strong>{result.orderId}</strong></p>
          <div className="confirmed-order-lines">
            {result.items.map((item, index) => <div className="order-total-line" key={`${item.productId}-${index}`}>
              <span>{item.qty} × {item.name}</span><span>{formatEuro(item.lineTotalCents)}</span>
            </div>)}
            <div className="order-total-line"><span>Spedizione</span><span>{formatEuro(result.shippingCents)}</span></div>
            <div className="order-total-line order-total-final"><strong>Totale pagato</strong><strong>{formatEuro(result.totalCents)}</strong></div>
          </div>
          {cartUpdated && <p className="success-meta">Gli articoli acquistati sono stati rimossi dal carrello.</p>}
        </>}
      </div>
      <div className="success-actions">
        {!loading && !paid && sessionId && <button type="button" className="cart-checkout-button" onClick={() => setRetry(n => n + 1)}>Ricontrolla pagamento</button>}
        <Link className="shop-back-link" to={paid ? '/shop' : '/shop/cart'}>{paid ? 'Continua a scoprire i prodotti' : 'Torna al carrello'}</Link>
        <Link className="shop-back-link" to="/">Menu pizzeria</Link>
      </div>
    </section>
  );
};

export default SuccessPage;
